<template>
  <v-container class="modern-stock-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-boxes</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Inventario
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Existencia global y valorización de activos</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-stock :disabled="loading" :family="family" :store="store" :is-critical="alert" :search="search"/>
      </div>
    </v-row>

    <!-- Filter Section (Master Pattern) -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Grupo / Familia</div>
          <v-select
            v-model="family"
            :items="families"
            item-text="name"
            item-value="id"
            placeholder="Todas las Familias"
            outlined dense hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-select"
            prepend-inner-icon="fas fa-filter"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Bodega</div>
          <v-select
            v-model="store"
            :items="stores"
            item-text="name"
            item-value="id"
            placeholder="Todas las Bodegas"
            outlined dense hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-select"
            prepend-inner-icon="fas fa-warehouse"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Stock Crítico</div>
          <v-select
            v-model="alert"
            :items="alertOptions"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-select"
            prepend-inner-icon="fas fa-exclamation-triangle"
            @click:clear="clearAlert"
            @input="logAlert"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="computedHeaders"
        :items="articles"
        :options.sync="options"
        :server-items-length="parseInt(total)"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Search Bar in Top Slot (Master Pattern) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por código, nombre o descripción..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              clearable
              :disabled="loading"
              class="modern-search-field flex-grow-1"
              @keyup="searchTimeOut"
            />
          </div>
        </template>

        <template v-slot:item.sku="{ item }">
          <div class="d-flex align-center">
            <v-tooltip top v-if="alertCritic(item)" color="error">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" class="mr-2 error--text animate-pulse" small>fas fa-exclamation-triangle</v-icon>
              </template>
              <span>Stock Crítico: Menor a {{item.critic}} {{item.unit_name}}</span>
            </v-tooltip>
            <span class="font-weight-black primary--text tracking-tighter">{{item.sku}}</span>
          </div>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <div>
              <div class="subtitle-2 font-weight-bold lh-1-2 mb-1 " style="max-width: 300px">{{item.name}}</div>
              <v-chip v-if="item.cost" x-small color="info lighten-5" label class="info--text font-weight-black">
                <v-icon x-small class="mr-1">fas fa-link</v-icon>
                {{item.cost.name}}
              </v-chip>
            </div>
          </div>
        </template>

        <template v-slot:item.unit_name="{ item }">
          <v-chip x-small outlined color="grey" class="font-weight-bold uppercase">{{item.unit_name}}</v-chip>
        </template>

        <template v-slot:item.total_stock="{ item }">
          <div class="d-flex align-center justify-end">
            <span class="text-h6 font-weight-black mr-2">{{totalStock(item) | number('0,0.00')}}</span>
            <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card existence-menu"> 
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon x-small v-bind="attrs" v-on="on" class="grey--text">
                  <v-icon small>fas fa-chevron-circle-down</v-icon>
                </v-btn>
              </template>
              <v-card flat class="pa-0">
                <div class="px-4 py-2 border-bottom bg-lightgrey">
                  <span class="caption font-weight-black uppercase tracking-widest grey--text text--darken-2">Distribución por Bodega</span>
                </div>
                <div v-if="item.stocks.length > 0">
                  <v-simple-table dense class="modern-simple-table">
                    <tbody>
                      <tr v-for="register in item.stocks" :key="register.id">
                        <td :class="register.add_stock ? '' : 'error--text font-weight-bold'">{{register.store_name}}</td>
                        <td class="text-end font-weight-black">{{register.qty | number('0,0.00')}}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
                <div v-else class="pa-4 text-center caption grey--text font-italic">Sin existencias registradas</div>
              </v-card>
            </v-menu>
          </div>
        </template>

        <template v-slot:item.compromised="{ item }">
          <div class="text-end">
            <span v-if="item.compromised > 0" class="caption font-weight-bold orange--text text--darken-3">
              {{item.compromised | number('0,0.00')}}
            </span>
            <span v-else class="caption grey--text opacity-50">—</span>
          </div>
        </template>

        <template v-slot:item.avg="{ item }">
          <div class="text-end font-weight-bold grey--text text--darken-1">{{item.avg | moneda}}</div>
        </template>

        <template v-slot:item.total_price="{ item }">
          <div class="text-end font-weight-black primary--text">{{totalStock(item) * item.avg | moneda}}</div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <v-tooltip left content-class="premium-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon small color="primary" 
                  v-bind="attrs" v-on="on"
                  @click="$router.push({name:'DetailedArticle', params:{id:item.id}})"
                  class="modern-action-btn"
                >
                  <v-icon small>fas fa-external-link-alt</v-icon>
                </v-btn>
              </template>
              <span>Ver ficha detallada</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showDialog" max-width="500"></v-dialog>
  </v-container>
</template>

<script>
import {fetchArticles,fetchFamilies,fetchStores} from '@/api/stocks'
import notifications from '@/utils/notifications'
import ExportStock from '@/components/stocks/ExportStock'

export default {
  name:'Stock',
  components:{ExportStock},
  data() {
    return {
      loading: false,
      family:'',
      families:[],
      store:'',
      stores:[],
      alert:'',
      alertOptions:[
        {text:'Solo Stock Crítico', value:true},
        {text:'Sin Stock Crítico', value:false}
      ],
      articles:[],
      article:'',
      timer:'',
      options:{},
      search:'',
      total:'',
      showDialog:false,
      headers: [
      {
        text: 'Código',
        align: 'start',
        value: 'sku',
        class: 'table-header2',
        show:true
      },
      {
        text: 'Nombre',
        align: 'start',
        value: 'name',
        class: 'table-header2',
        show:true
      },
      
      {
        text: 'Unid.',
        align: 'start',
        value: 'unit_name',
        class: 'table-header2',
        show:true
      },
      {
        text: 'Exist.',
        align: 'start',
        value: 'total_stock',
        class: 'table-header2',
        sortable: false,
        align:'end',
        show:true
      },
      {
        text: 'Comp.',
        align: 'start',
        value: 'compromised',
        class: 'table-header2',
        sortable: false,
        align:'end',
        show:true
      },
      {
        text: 'Valor Prom.',
        align: 'start',
        value: 'avg',
        class: 'table-header2',
        sortable: false,
        align:'end',
        show:false
      },
      {
        text: 'Valor Total.',
        align: 'start',
        value: 'total_price',
        class: 'table-header2',
        sortable: false,
        align:'end',
        show:false
      },
      { text: '',value:'actions',class: 'table-header2',align:'center',sortable: false,show:true}
    ],
    }
  },
  methods:{
    clearAlert() {
      console.log('[STOCK] clearAlert called, setting alert to empty string')
      this.alert = ''
    },
    logAlert(value) {
      console.log('[STOCK] alert changed to:', value, 'type:', typeof value)
    },
    totalStock(item){
      if(this.store){
        const filteredStocks = item.stocks.filter(i=>i.store==this.store)
        return filteredStocks.reduce((acc, val)=>{ return acc + val.qty; }, 0)
      }
      else return item.stocks.filter(i=>i.add_stock).reduce((acc, val)=>{ return acc + val.qty }, 0)
    },
    alertCritic(item){
      return this.totalStock(item)<item.critic&&item.alert
    },
    async fetch(){
      this.loading=true
      const {sortBy, sortDesc, page, itemsPerPage }=this.options
      let sort=''
      if(sortBy[0]=='family_name'){
        sort = 'family__name'
      }
      else if(sortBy[0]=='unit_name'){
        sort = 'unit__name'
      }
      else {
        sort = sortBy[0]
      }
      if (!this.family) this.family=''
      if (!this.store) this.store=''

      // Convertir alert a string vacío si es null/undefined
      const alertValue = (this.alert !== null && this.alert !== undefined && this.alert !== '') ? this.alert : ''

      const query=`ordering=${sortDesc[0]?sort:'-' + sort}&&page=${page}&&page_size=${itemsPerPage}&&search=${this.search}&&family=${this.family}&&stocks__store=${this.store}&&status=true&&is_critical=${alertValue}`
      await fetchArticles(query)
      .then(res=>{
        this.total=res.count
        this.articles=res.results
      })
      .catch((err)=>{
        notifications.showError('Error cargando artículos '+err)
      })
      this.loading=false
    },
    searchTimeOut() {  
      //Función para hacer búsqueda lazy
      if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
      }
      this.timer = setTimeout(() => {
          this.fetch()
      }, 800);
    },
    update(item){
      this.article=item
      this.showDialog=true
    },
  },
  watch:{
    async family(){
      this.fetch()
    },
    async store(){
      this.fetch()
    },
    async alert(newVal){
      // Forzar a string vacío si es null/undefined
      if (newVal === null || newVal === undefined) {
        this.alert = ''
      }
      this.fetch()
    },
    options:{
      handler:'fetch',
      deep:true
    }
  },
  async mounted(){
    this.fetch()
    this.loading=true
    this.families=await fetchFamilies()
    this.stores=await fetchStores()
    this.loading=false
  },
  computed:{
    computedHeaders(){
      return this.headers.filter(h=>h.show&&this.store||!this.store)
    }
  }
}
</script>

<style>

</style>